import { useEffect, useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { useMutation } from 'react-query';

import {
  deleteServiceProduct,
  postServiceProducts,
  putServiceProduct,
  putJob,
  deleteJobLabour,
  postJobLabour,
} from 'queries';
import { Condition, JsonData, JsonDataItem } from 'types';
import { parseErrors, Toast } from 'helpers';

export const useServiceProductsUpdate = () => {
  const [pendingProdcutsToAdd, setPendingProductsToAdd] = useState<[number, JsonData] | null>(null);
  const [productUpdateQueue, setProductUpdateQueue] = useState<number>(0);
  const handleMutationError = (error: JsonDataItem) => {
    Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
  };

  const { mutate: updateLegacyJob, isLoading: isLegacyUpdating } = useMutation(putJob, {
    onError: handleMutationError,
  });
  const { mutate: addLegacyLabourRate, isLoading: isLegacyRateAdding } = useMutation(postJobLabour, {
    onError: handleMutationError,
  });
  const { mutate: deleteLegacyLabourRate, isLoading: isLegacyRateDeleting } = useMutation(deleteJobLabour, {
    onError: handleMutationError,
  });

  const { mutate: updateProduct, isLoading: isUpdating } = useMutation(putServiceProduct, {
    onSuccess: () => {
      setProductUpdateQueue((prev) => prev - 1);
    },
    onError: handleMutationError,
  });

  const {
    mutate: deleteProduct,
    isLoading: isDeleting,
    isSuccess: isProductDeleted,
  } = useMutation(deleteServiceProduct, {
    onSuccess: () => {
      setProductUpdateQueue((prev) => prev - 1);
    },
    onError: handleMutationError,
  });

  const { mutate: addProduct, isLoading: isAdding } = useMutation(postServiceProducts, {
    onSuccess: () => {
      setProductUpdateQueue((prev) => prev - 1);
    },
    onError: handleMutationError,
  });

  const compareAndUpdate = (
    newProducts: JsonData,
    oldProducts: JsonData,
    serviceId: number,
    requestedAppointment: string | undefined,
  ) => {
    const oldProductsMap = new Map<number, JsonDataItem>(
      oldProducts.map((product: JsonDataItem) => [product.product.ID, product]),
    );
    const productsToAdd = newProducts?.filter((product) => !oldProductsMap.has(parseInt(product.id)));
    const productsToRemove = Array.from(oldProductsMap.keys()).filter(
      (id) => !newProducts.some((product) => Number(product.id) === id),
    );

    setProductUpdateQueue(productsToRemove.length + productsToAdd.length);

    if (productsToRemove.length) {
      productsToRemove.forEach((productId: number) => {
        deleteProduct({
          serviceId,
          productId: oldProducts.find((item: JsonDataItem) => item.product.ID === productId)?.product
            .service_product_ID,
        });
      });
    }

    if (productsToAdd.length) {
      // call addProduct in parallel only if there are no products to remove
      !productsToRemove.length
        ? addProduct({
            serviceId,
            products: productsToAdd.map((product) => {
              return {
                product_id: product.id,
                quantity: product.qty,
                appointment: isValid(parseISO(product.date)) ? format(product.date, 'yyyy-MM-dd HH:mm') : product.date,
              };
            }),
          })
        : // set pending products and job ID to add once product removal is completed
          setPendingProductsToAdd([serviceId, productsToAdd]);
    }

    oldProducts.forEach((oldProduct: JsonDataItem) => {
      const newProduct = newProducts.find((product: JsonDataItem) => product.id === oldProduct.product.ID.toString());
      if (newProduct) {
        const oldQty = oldProduct.product.quantity;
        const newQty = newProduct.qty;
        const oldDate = isValid(parseISO(requestedAppointment || oldProduct.service_technicians[0].appointment))
          ? format(parseISO(requestedAppointment || oldProduct.service_technicians[0].appointment), 'yyyy-MM-dd HH:mm')
          : oldProduct.service_technicians[0]?.appointment;
        const newDate = isValid(parseISO(newProduct.date))
          ? format(newProduct.date, 'yyyy-MM-dd HH:mm')
          : newProduct.date;

        if (oldQty !== newQty || oldDate !== newDate) {
          setProductUpdateQueue((prev) => prev + 1);
          updateProduct({
            serviceId,
            productId: oldProduct.product.service_product_ID,
            qty: newProduct.qty,
            date: newDate,
          });
        }
      }
    });
  };

  useEffect(() => {
    // check for pending products to add and add them once product removal is completed
    isProductDeleted &&
      pendingProdcutsToAdd &&
      (addProduct({
        serviceId: pendingProdcutsToAdd[0],
        products: pendingProdcutsToAdd[1].map((product) => {
          return {
            product_id: product.id,
            quantity: product.qty,
            appointment: format(product.date, 'yyyy-MM-dd HH:mm'),
          };
        }),
      }),
      setPendingProductsToAdd(null));
  }, [isProductDeleted]);

  const isLoading =
    isDeleting || isAdding || isUpdating || isLegacyUpdating || isLegacyRateAdding || isLegacyRateDeleting;

  const updateServiceProducts = (props: JsonDataItem) => {
    const { newProducts, initialProducts, serviceId, requestedAppointment } = props;

    return compareAndUpdate(newProducts, initialProducts, serviceId, requestedAppointment);
  };

  // TODO: remove legacy job update logic when support is over
  const updateLegacyProducts = (props: JsonDataItem) => {
    const { serviceId, initialData, newData } = props;

    // check for removed consultations
    const findRemovedConsultations = (initialConsultations: JsonData, newServices: JsonData) => {
      const initialIds = initialConsultations.map((consultation) => consultation.ID);
      const newConsultationIds = newServices
        .filter((service) => service.id.startsWith('existing_consultation_'))
        .map((service) => parseInt(service.id.replace('existing_consultation_', ''), 10));

      return initialIds.filter((id) => !newConsultationIds.includes(id));
    };
    const removedIds = findRemovedConsultations(initialData.consultations, newData);
    removedIds.forEach((labourId) => deleteLegacyLabourRate({ serviceId, labourId }));

    // check for added consultations
    const findNewConsultations = (initialConsultations: JsonData, newServices: JsonData) => {
      const initialIds = initialConsultations.map((consultation) => consultation.ID);
      const newConsultations = newServices
        .filter((service) => service.id.startsWith('new_consultation_'))
        .filter((service) => {
          const newConsultationId = parseInt(service.id.replace('new_consultation_', ''), 10);
          return !initialIds.includes(newConsultationId);
        });

      return newConsultations.map((service) => ({
        date: service.date || '',
        hours: service.qty,
      }));
    };
    const newConsultations = findNewConsultations(initialData.consultations, newData);
    newConsultations.forEach((consultation) =>
      addLegacyLabourRate({
        serviceId,
        date: format(new Date(consultation.date), 'yyyy-MM-dd'),
        hours: consultation.hours,
      }),
    );

    // check for legacy flags change (sds, weekend)
    const findServiceFlagChanges = (initialData: JsonDataItem, newServices: JsonData): JsonDataItem | null => {
      const sameDayService =
        newServices.find((service) => service.id === 'sds')?.qty === 1 ? Condition.Yes : Condition.No;
      const weekendService =
        newServices.find((service) => service.id === 'weekend')?.qty === 1 ? Condition.Yes : Condition.No;
      if (sameDayService !== initialData.job.sameday_service || weekendService !== initialData.job.weekend_service) {
        return { sameDayService: sameDayService, weekendService: weekendService };
      }
      return null;
    };
    const serviceFlagChanges = findServiceFlagChanges(initialData, newData);
    serviceFlagChanges && updateLegacyJob({ serviceId, ...serviceFlagChanges });
  };

  return { updateServiceProducts, updateLegacyProducts, isLoading, productUpdateQueue };
};
